import { api } from './client';
import type { Page } from './catalogs';

export interface TaxInvoiceOutgoingItem {
  id: string;
  line_number: number;
  nomenclature_id: string | null;
  description: string;
  ukt_zed_code: string | null;
  uom_code: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount: string;
  vat_amount: string;
}

export interface TaxInvoiceOutgoingListItem {
  id: string;
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contractor_name: string;
  total_amount: string;
  erpn_status: string;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
}

export interface TaxInvoiceOutgoing extends TaxInvoiceOutgoingListItem {
  is_correction: boolean;
  contract_id: string | null;
  contractor_edrpou: string | null;
  contractor_inn: string | null;
  invoice_type: string;
  base_20: string;
  vat_20: string;
  base_7: string;
  vat_7: string;
  base_14: string;
  vat_14: string;
  base_0: string;
  base_exempt: string;
  erpn_registration_date: string | null;
  base_document_type: string | null;
  base_document_id: string | null;
  posted_at: string | null;
  items: TaxInvoiceOutgoingItem[];
}

export interface TaxInvoiceOutgoingCreate {
  organization_id: string;
  base_document_type: 'doc.goods_sales';
  base_document_id: string;
  invoice_number?: string;
  invoice_date?: string;
}

export const taxInvoicesOutgoing = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<TaxInvoiceOutgoingListItem>>('/tax-invoices-outgoing', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}`).then((r) => r.data),
  createFromSale: (payload: TaxInvoiceOutgoingCreate) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing/from-sale', payload)
      .then((r) => r.data),
  createCorrectionFromReturn: (payload: TaxInvoiceOutgoingCorrectionCreate) =>
    api.post<TaxInvoiceOutgoing>('/tax-invoices-outgoing/correction-from-return', payload)
      .then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-invoices-outgoing/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/post`)
      .then((r) => r.data),
  unpost: (id: string) =>
    api.post<TaxInvoiceOutgoing>(`/tax-invoices-outgoing/${id}/unpost`)
      .then((r) => r.data),
};

// ==================== Incoming ====================

export interface TaxInvoiceIncomingItem {
  id: string;
  line_number: number;
  nomenclature_id: string | null;
  description: string;
  ukt_zed_code: string | null;
  uom_code: string | null;
  quantity: string;
  price_without_vat: string;
  vat_rate_id: string;
  base_amount: string;
  vat_amount: string;
}

export interface TaxInvoiceIncomingListItem {
  id: string;
  organization_id: string;
  invoice_number: string;
  invoice_date: string;
  contractor_id: string;
  contractor_name: string;
  total_amount: string;
  is_registered_erpn: boolean;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
}

export interface TaxInvoiceIncoming extends TaxInvoiceIncomingListItem {
  contract_id: string | null;
  contractor_edrpou: string | null;
  contractor_inn: string | null;
  is_correction: boolean;
  invoice_type: string;
  base_20: string;
  vat_20: string;
  base_7: string;
  vat_7: string;
  base_14: string;
  vat_14: string;
  base_0: string;
  base_exempt: string;
  registration_date: string | null;
  base_document_type: string | null;
  base_document_id: string | null;
  posted_at: string | null;
  items: TaxInvoiceIncomingItem[];
}

export interface TaxInvoiceIncomingCreate {
  organization_id: string;
  base_document_type: 'doc.goods_receipts';
  base_document_id: string;
  invoice_number: string;
  invoice_date: string;
  is_registered_erpn?: boolean;
  registration_date?: string;
}

export interface TaxInvoiceOutgoingCorrectionCreate {
  organization_id: string;
  return_id: string;
  corrected_invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  correction_reason?: string | null;
}

export interface TaxInvoiceIncomingCorrectionCreate {
  organization_id: string;
  return_id: string;
  corrected_invoice_id: string;
  invoice_number: string;
  invoice_date: string;
  is_registered_erpn?: boolean;
  registration_date?: string;
}

export const taxInvoicesIncoming = {
  list: (params?: {
    organization_id?: string;
    contractor_id?: string;
    posted?: boolean;
    limit?: number;
  }) =>
    api.get<Page<TaxInvoiceIncomingListItem>>('/tax-invoices-incoming', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}`).then((r) => r.data),
  createFromReceipt: (payload: TaxInvoiceIncomingCreate) =>
    api.post<TaxInvoiceIncoming>('/tax-invoices-incoming/from-receipt', payload)
      .then((r) => r.data),
  createCorrectionFromReturn: (payload: TaxInvoiceIncomingCorrectionCreate) =>
    api.post<TaxInvoiceIncoming>('/tax-invoices-incoming/correction-from-return', payload)
      .then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/tax-invoices-incoming/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}/post`)
      .then((r) => r.data),
  unpost: (id: string) =>
    api.post<TaxInvoiceIncoming>(`/tax-invoices-incoming/${id}/unpost`)
      .then((r) => r.data),
};
